import React, {
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import cns from 'classnames';

import FeedbackIcon from '../../../GoogleIcons/Filled/FeedbackIcon';
import QuestionAnswerIcon from '../../../GoogleIcons/Filled/QuestionAnswerIcon';
import ThumbUpIcon from '../../../GoogleIcons/Filled/ThumbUpIcon';
import ReportIcon from '../../../GoogleIcons/Filled/ReportIcon';

const EMP_MED = 'MEDIUM';
const EMP_LOW = 'LOW';

/**
Pannello per chiedere la conferma di lettura di un messaggio.

_Quando si utilizza?_
Può essere utile all'interno di un processo in cui accadono eventi che richiedono una particolare attenzione.

Tramite la modale di feedback si possono veicolare 4 tipologie di messaggi:

- _Informazione_: per comunicare un'informazione utile.
- _Successo_: per informare che qualcosa è avvenuto correttamente.
- _Attenzione_: per avvertire l'utente di una condizione che potrebbe causare problemi in futuro.
- _Allarme/Errore_: per avvertire l'utente di un problema/errore presente.

Per una questione di facilità di lettura e comprensione i messaggi devono essere sintetici.
Può esserti utile consultare la sezione dedicata ai Testi. Si può anche inserire un link a maggiori dettagli.

L'icona può essere cambiata specificandone una ma è sempre meglio farlo in accordo con la UX, le icone utilizzabili devono comunque essere "filled".
*/
const FeedbackPanel = ({
  type, title, message, emphasis, icon,
}) => {
  const Icon = useMemo(() => {
    if (icon) {
      return icon;
    }
    if (type === 'success' || type === 'success-partial') {
      return ThumbUpIcon;
    }
    if (type === 'warning') {
      return FeedbackIcon;
    }
    if (type === 'alarm') {
      return ReportIcon;
    }
    return QuestionAnswerIcon;
  }, [icon, type]);

  return (
    <div className={cns('v2-feedback-panel', `v2-feedback-panel--${type}`, `v2-feedback-panel--emphasis-${emphasis.toLowerCase()}`)}>
      <Icon className="v2-feedback-panel__icon" />
      <div className="v2-feedback-panel__content">
        <h1>{title}</h1>
        <div className="v2-feedback-panel__message">{message}</div>
      </div>
    </div>
  );
};

FeedbackPanel.Emphasis = {
  LOW: EMP_LOW,
  MEDIUM: EMP_MED,
};

FeedbackPanel.propTypes = {
  /** Indica la tipologia del feedback */
  type: PropTypes.oneOf(['info', 'alarm', 'warning', 'success', 'success-partial']),
  /** Titolo del feedback */
  title: PropTypes.string.isRequired,
  /** messaggio del feedback */
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** Enfasi del bottone */
  emphasis: PropTypes.oneOf([FeedbackPanel.Emphasis.LOW, FeedbackPanel.Emphasis.MEDIUM]),
  /** Indica l'icona da sostituire a quella di default */
  icon: PropTypes.elementType,
};

FeedbackPanel.defaultProps = {
  type: 'info',
  emphasis: FeedbackPanel.Emphasis.LOW,
  icon: undefined,
};

export default FeedbackPanel;
